import { useState, useEffect, useMemo } from "react";
import { useTheme, THEMES, Sp, Btn, Card, CardHead, SH, Tile, Insight, MiniStat, Overlay, Tbl, Td, LineChart, HBar, FilterPill, Input, Select, Textarea, toast, Picto } from "./ui";
import { computeMetrics, feedbackMetrics, loadReviews, NOT_YET } from "./lib/metrics";
import { listStats } from "./lib/statsStore";
import { listHkps, addHkp as storeAddHkp, updateHkp, deleteHkp as storeDeleteHkp, loadHkpsCache } from "./lib/hkpStore";
import { listPatients, loadPatientsCache } from "./lib/patientStore";
import { getBehandler } from "./lib/settingsStore";
import { listRecalls, markKontaktiert, loadRecallsCache, addRecall, syncRecallsFromPatients, setRecallStatus } from "./lib/recallStore";
import { listApts as listAptsStore } from "./lib/aptStore";
import { reviewStore } from "./lib/docStore";
import { loadPulse, eNPS, MIN_VOTES, pulsePeriode } from "./lib/teamPulse";
import { sendMails, findPatientEmail, praxisAbsender } from "./lib/mailer";
import PatientPicker from "./PatientPicker";
import BewertungenScreen from "./BewertungenScreen";
import { serverActive } from "./lib/mode";

// ═══════════════════════════════════════════════════════════════════════════
// SCORECARD — Patientenbewertung (auch in der Patientenliste genutzt)
// ═══════════════════════════════════════════════════════════════════════════

const daysSince = (d) => {
  try {
    if(!d||d==="—") return null;
    const [dd,mm,yy] = d.split(".");
    const dt = new Date(2000+parseInt(yy), parseInt(mm)-1, parseInt(dd));
    return Math.round((Date.now()-dt.getTime())/86400000);
  } catch { return null; }
};

export function scoreFactors(p) {
  const f = [];
  let s = 50;
  if(p.status==="Aktiv"){ s+=20; f.push({label:"Status: Aktiv",pts:+20}); }
  else if(p.status==="Erinnern"){ s-=5; f.push({label:"Status: Erinnern",pts:-5}); }
  else if(p.status==="Inaktiv"){ s-=25; f.push({label:"Status: Inaktiv",pts:-25}); }
  if(p.prop){ s+=15; f.push({label:"Prophylaxe-Teilnahme",pts:+15}); }
  else { f.push({label:"Keine Prophylaxe-Teilnahme",pts:0}); }
  if(p.pzr&&p.pzr.includes("2×")){ s+=5; f.push({label:"PZR 2× jährlich",pts:+5}); }
  if(p.next==="überfällig"){ s-=15; f.push({label:"Recall überfällig",pts:-15}); }
  const days = daysSince(p.last);
  if(days!=null){
    if(days<=90){ s+=10; f.push({label:`Letzter Besuch vor ${days} Tagen`,pts:+10}); }
    else if(days<=180){ s+=5; f.push({label:`Letzter Besuch vor ${days} Tagen`,pts:+5}); }
    else if(days>365){ s-=10; f.push({label:`Letzter Besuch vor ${days} Tagen`,pts:-10}); }
    else { f.push({label:`Letzter Besuch vor ${days} Tagen`,pts:0}); }
  }
  if((p.history||[]).length>=2){ s+=5; f.push({label:"Regelmäßige Behandlungshistorie",pts:+5}); }
  if(p.src==="Freunde / Familie"){ s+=5; f.push({label:"Empfehlungspatient",pts:+5}); }
  return { score: Math.max(0,Math.min(100,s)), factors: f };
}

export const computeScore = (p) => scoreFactors(p).score;

export const scoreMeta = (score) => {
  if(score>=80) return { grade:"A", label:"Top",       bg:"#E1F5EE", col:"#085041", bar:"#0F6E56", empf:"Bestandspflege — ideal für Empfehlungsprogramm" };
  if(score>=60) return { grade:"B", label:"Stabil",    bg:"#E6F1FB", col:"#0C447C", bar:"#185FA5", empf:"Regulärer Recall ausreichend" };
  if(score>=40) return { grade:"C", label:"Gefährdet", bg:"#FAEEDA", col:"#633806", bar:"#BA7517", empf:"Recall priorisieren, aktiv nachfassen" };
  return            { grade:"D", label:"Kritisch",  bg:"#FCEBEB", col:"#791F1F", bar:"#D85A30", empf:"Sofort kontaktieren — Reaktivierungsangebot" };
};

const SCORE_DARK = {
  A:{bg:"rgba(53,201,155,.16)",col:"#7DE8C6"}, B:{bg:"rgba(96,165,250,.15)",col:"#93C5FD"},
  C:{bg:"rgba(250,199,117,.14)",col:"#F5C97B"}, D:{bg:"rgba(255,118,110,.15)",col:"#FF9E97"},
};

export const ScoreBadge = ({score,showLabel}) => {
  const m = scoreMeta(score);
  const dk = useTheme()==="dark" ? SCORE_DARK[m.grade] : m;
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,background:dk.bg,color:dk.col,borderRadius:10,padding:"2px 8px",fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>
      {m.grade} · {score}{showLabel&&<span style={{fontWeight:400}}> {m.label}</span>}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SIGNATUR-BAUSTEINE — farbige Kacheln, Pipeline-Leiste, Verteilungsbalken
// Farbsprache: g=gut/erledigt, b=läuft, a=Achtung, r=dringend, pu=Kasse, n=neutral
// ═══════════════════════════════════════════════════════════════════════════
const TONES_LIGHT = {
  g:{bg:"#E1F5EE",fg:"#085041",bar:"#0F6E56"}, b:{bg:"#E6F1FB",fg:"#0C447C",bar:"#185FA5"},
  a:{bg:"#FAEEDA",fg:"#633806",bar:"#BA7517"}, r:{bg:"#FCEBEB",fg:"#791F1F",bar:"#D85A30"},
  pu:{bg:"#EEEDFE",fg:"#3C3489",bar:"#6D5FE0"}, n:{bg:"var(--mf-bg-alt)",fg:"var(--mf-muted)",bar:"var(--mf-border2)"},
};
const TONES_DARK = {
  g:{bg:"rgba(53,201,155,.16)",fg:"#7DE8C6",bar:"#35C99B"}, b:{bg:"rgba(96,165,250,.15)",fg:"#93C5FD",bar:"#60A5FA"},
  a:{bg:"rgba(250,199,117,.14)",fg:"#F5C97B",bar:"#E0A93F"}, r:{bg:"rgba(255,118,110,.15)",fg:"#FF9E97",bar:"#F0705F"},
  pu:{bg:"rgba(167,139,250,.15)",fg:"#C4B5FD",bar:"#A78BFA"}, n:{bg:"var(--mf-bg-alt)",fg:"var(--mf-muted)",bar:"var(--mf-border2)"},
};
const useTones = () => (useTheme()==="dark" ? TONES_DARK : TONES_LIGHT);

// Altersgruppen sind geordnet, aber nicht "gut" oder "schlecht" — deshalb
// KEINE Ampelfarben, sondern eine Sequenz aus einem Farbton (jung → alt).
// Hell: hell → dunkel, Dunkel: gedämpft → hell; alle Stufen ≥ 3:1 zum
// Hintergrund, damit auch der erste Balken sichtbar bleibt.
const AGE_RAMP_LIGHT = ["#2AA484","#1E9375","#12886B","#0F6E56","#0B5443"];
const AGE_RAMP_DARK  = ["#1B7A60","#229574","#35C99B","#63DAB6","#93E8CD"];
const useAgeRamp = () => (useTheme()==="dark" ? AGE_RAMP_DARK : AGE_RAMP_LIGHT);

// Farbige KPI-Kachel (tone="n" = neutral wie bisher)
function StatTile({ tone="n", v, l, d, dc, active, onClick }) {
  const T = useTones()[tone];
  const [hov,setHov] = useState(false);
  const neutral = tone==="n";
  return (
    <div onClick={onClick} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{background:neutral?(hov?"var(--mf-bg-alt)":"var(--mf-bg)"):T.bg,
        border:`1px solid ${active?T.bar:neutral?"var(--mf-border)":"transparent"}`,
        borderRadius:14,padding:"11px 13px",cursor:onClick?"pointer":"default",transition:"all .15s",position:"relative",
        boxShadow:hov&&onClick?"0 2px 12px rgba(0,0,0,.06)":"none"}}>
      <div style={{fontSize:20,fontWeight:600,color:neutral?"var(--mf-text)":T.fg}}>{v}</div>
      <div style={{fontSize:10,color:neutral?"var(--mf-muted)":T.fg,opacity:neutral?1:.9,marginTop:1}}>{l}</div>
      {d&&<div style={{fontSize:10,marginTop:3,color:dc==="up"?"var(--mf-primary)":dc==="dn"?"var(--mf-neg)":"var(--mf-muted)"}}>{d}</div>}
      {onClick&&<i className="ti ti-chevron-right" style={{position:"absolute",top:10,right:8,fontSize:11,color:hov?(neutral?"var(--mf-primary)":T.bar):"var(--mf-border2)",transition:"color .15s"}}/>}
    </div>
  );
}

// Pipeline-/Trichter-Leiste: farbige Stufen mit Anzahl + Zusatzwert, klickbar
function PipelineBar({ stages, activeKey, onPick }) {
  const T = useTones();
  const max = Math.max(1, ...stages.map(s=>s.count));
  return (
    <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
      {stages.map((s,i)=>{
        const c = T[s.tone]; const active = activeKey===s.key;
        return (
          <div key={s.key} style={{display:"flex",alignItems:"stretch",flex:"1 1 0",minWidth:104}}>
            <div onClick={()=>onPick&&onPick(active?"":s.key)} style={{flex:1,cursor:onPick?"pointer":"default",background:c.bg,borderRadius:11,padding:"9px 10px",
              border:`1px solid ${active?c.bar:"transparent"}`,transition:"all .15s"}}>
              <div style={{fontSize:9.5,fontWeight:600,color:c.fg,marginBottom:5,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.label}</div>
              <div style={{fontSize:19,fontWeight:700,color:c.fg,lineHeight:1}}>{s.count}</div>
              <div style={{height:3,borderRadius:2,background:c.bar,opacity:.55,marginTop:6,width:`${Math.max(8,s.count/max*100)}%`}}/>
              {s.value!=null&&<div style={{fontSize:9.5,color:c.fg,opacity:.8,marginTop:5,whiteSpace:"nowrap"}}>{s.value}</div>}
            </div>
            {i<stages.length-1&&<div style={{display:"flex",alignItems:"center",color:"var(--mf-border2)",padding:"0 1px"}}><i className="ti ti-chevron-right" style={{fontSize:12}}/></div>}
          </div>
        );
      })}
    </div>
  );
}

// Segmentierter Verteilungsbalken (Scorecard A|B|C|D)
function DistBar({ segments, total, activeKey, onPick }) {
  const T = useTones();
  return (
    <div>
      <div style={{display:"flex",height:28,borderRadius:9,overflow:"hidden",gap:2,background:"var(--mf-bg-alt)"}}>
        {segments.filter(s=>s.count>0).map(s=>{
          const c = T[s.tone]; const pct = total?s.count/total*100:0;
          return (
            <div key={s.key} onClick={()=>onPick&&onPick(activeKey===s.key?"":s.key)} title={`${s.label}: ${s.count}`}
              style={{width:`${pct}%`,background:c.bar,cursor:onPick?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",
                opacity:activeKey&&activeKey!==s.key?.35:1,transition:"opacity .15s"}}>
              {pct>7&&<span style={{fontSize:10,fontWeight:700,color:"#fff"}}>{s.count}</span>}
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",gap:14,marginTop:8,flexWrap:"wrap"}}>
        {segments.map(s=>{const c=T[s.tone];const active=activeKey===s.key;return (
          <div key={s.key} onClick={()=>onPick&&onPick(active?"":s.key)} style={{display:"flex",alignItems:"center",gap:5,fontSize:10.5,color:"var(--mf-muted)",cursor:onPick?"pointer":"default",opacity:activeKey&&!active?.5:1}}>
            <span style={{width:10,height:10,borderRadius:3,background:c.bar}}/>{s.label} <strong style={{color:"var(--mf-text)"}}>{s.count}</strong>
          </div>
        );})}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DEMO-DATEN
// ═══════════════════════════════════════════════════════════════════════════

// KPI-Werte je Zeitraum für die 12 Übersichts-Kacheln
const KPI_META = {
  hkp:       { l:"HKP-Akzeptanzrate",        icon:"ti-file-check" },
  umsatzart: { l:"Umsatz (importiert)",      icon:"ti-chart-pie" },
  leistungen:{ l:"Leistungsspektrum",        icon:"ti-list-numbers" },
  kassenmix: { l:"Kassen-/Privatmix",        icon:"ti-scale" },
  clv:       { l:"Ø Umsatz je Patient",      icon:"ti-trending-up" },
  igel:      { l:"IGeL-Konversionsrate",     icon:"ti-diamond" },
  noshow:    { l:"Ausfallquote (30 Tage)",   icon:"ti-user-x" },
  recall:    { l:"Recall-Rücklauf",          icon:"ti-bell-ringing" },
  churn:     { l:"Patienten-Churn",          icon:"ti-trending-down" },
  score:     { l:"Patienten-Score",          icon:"ti-target" },
  demografie:{ l:"Altersstruktur",           icon:"ti-users-group" },
  auslastung:{ l:"Auslastung (Woche)",       icon:"ti-chart-bar" },
  wartezeit: { l:"Ø Wartezeit auf Termin",   icon:"ti-clock" },
  wartezimmer:{ l:"Wartezimmer-Zeit",        icon:"ti-armchair" },
  dauer:     { l:"Behandlungsdauer Ist/Soll", icon:"ti-clock-hour-4" },
  behandler: { l:"Behandler-Auslastung",     icon:"ti-stethoscope" },
  feedback:  { l:"Feedback-Score",           icon:"ti-star" },
};

// Kurz-Erklärungen für Nicht-Zahnmediziner (Tooltip + Detailansicht)
const KPI_INFO = {
  hkp:       "Heil- und Kostenpläne: Behandlungs-Angebote, die Patient und Kasse genehmigen müssen. Quote = wie viele angenommen werden.",
  umsatzart: "Praxisumsatz aus deiner importierten PVS-Statistik, je Abrechnungsperiode.",
  leistungen:"Welche BEMA- und GOZ-Ziffern die Praxis abrechnet — nach Häufigkeit und nach Honorar. Zeigt, welche Leistungen den Umsatz wirklich tragen, und wie hoch der GOZ-Anteil am Honorar ist (gerechnet aus Ziffern, nicht geschätzt aus dem Versichertenstatus).",
  kassenmix: "Verhältnis gesetzlich zu privat versicherter Patienten — Privatpatienten tragen meist überproportional zum Umsatz bei.",
  clv:       "Durchschnittlicher Umsatz pro aktivem Patient in der letzten importierten Periode.",
  igel:      "Selbstzahlerleistungen (z.B. Bleaching): Wie viele Angebote führen zum Abschluss?",
  noshow:    "Anteil abgesagter Termine der letzten 30 Tage — jeder Ausfall ist unbezahlte Stuhlzeit.",
  recall:    "Wiedereinbestellung zur Prophylaxe (PZR): Wie viele kontaktierte Patienten buchen tatsächlich?",
  churn:     "Patienten, die der Praxis verloren gehen: letzter Besuch über 12 Monate her und kein Folgetermin geplant. Reaktivierung lohnt — ein zurückgewonnener Patient kostet weniger als ein neuer.",
  score:     "dida-Bewertung je Patient (0–100) aus Treue, Prophylaxe und Besuchsfrequenz — zeigt, wer Aufmerksamkeit braucht.",
  demografie:"Altersverteilung deines Patientenstamms aus den Geburtsdaten. Zeigt, welche Leistungen zum Stamm passen — Kinderprophylaxe und KFO bei jungen Jahrgängen, Zahnersatz und Parodontologie bei älteren — und ob die Praxis nachwächst.",
  auslastung:"Gebuchte Behandlungszeit im Verhältnis zur verfügbaren Zeit (Öffnungszeiten × Behandler), aktuelle Woche.",
  wartezeit: "Wie lange Patienten von der Buchung bis zum Termin warten.",
  wartezimmer:"Wie lange Patienten nach dem Check-in im Wartezimmer sitzen.",
  dauer:     "Geplante vs. tatsächliche Behandlungsdauer je Terminart.",
  behandler: "Auslastung im Vergleich über dein Team, aktuelle Woche.",
  feedback:  "Durchschnitt deiner Patientenbewertungen aus dem Bewertungsmodul.",
};

const OVERVIEW_SECTIONS = [
  { label:"Wirtschaft & Umsatz",  tiles:["hkp","umsatzart","leistungen","igel","kassenmix","clv"] },
  { label:"Patienten & Bindung",  tiles:["noshow","recall","churn","score","demografie"] },
  { label:"Betrieb & Team",       tiles:["auslastung","wartezeit","wartezimmer","dauer","behandler","feedback"] },
];

const HKP_STATUS = {
  entwurf:     { l:"Entwurf",            color:"n" },
  vorgelegt:   { l:"Patient vorgelegt",  color:"b" },
  eingereicht: { l:"Bei Kasse",          color:"pu" },
  genehmigt:   { l:"Kasse genehmigt",    color:"b" },
  angenommen:  { l:"Angenommen",         color:"g" },
  durchgefuehrt:{ l:"Durchgeführt",      color:"g" },
  abgelehnt:   { l:"Abgelehnt",          color:"r" },
  abgelaufen:  { l:"Abgelaufen",         color:"a" },
};

// Feedback auf Behandlerebene
const eur = (n) => "€ " + n.toLocaleString("de-DE");

// ═══════════════════════════════════════════════════════════════════════════
// KPI-DETAILANSICHTEN
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// KPI-DETAILANSICHTEN — komplett aus den echten Datenquellen berechnet.
// Fehlt die Quelle, gibt es einen ehrlichen Leerzustand statt Demo-Zahlen.
// ═══════════════════════════════════════════════════════════════════════════

function EmptyState({ hint, onAction, actionLabel }) {
  return (
    <div style={{padding:"18px 8px",textAlign:"center"}}>
      <i className="ti ti-database-off" style={{fontSize:24,color:"var(--mf-border2)"}}/>
      <div style={{fontSize:12,fontWeight:500,color:"var(--mf-text)",marginTop:6}}>Noch keine Datenquelle</div>
      <div style={{fontSize:11,color:"var(--mf-muted)",marginTop:4,lineHeight:1.5,maxWidth:380,margin:"4px auto 0"}}>{hint}</div>
      {onAction&&<div style={{marginTop:12}}><Btn color="g" onClick={onAction}>{actionLabel||"Daten importieren"}</Btn></div>}
    </div>
  );
}

function KpiBody({ kpi, m, onNavigate }) {
  const notYet = NOT_YET[kpi];
  if (notYet) return <EmptyState hint={notYet}/>;
  const importAction = onNavigate ? () => onNavigate("import") : undefined;

  const body = {
    churn: () => !m.churn.ok ? <EmptyState hint={m.churn.hint} onAction={importAction} actionLabel="Patienten importieren"/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={`${m.churn.pct} %`} l="Churn-Risiko" tone={m.churn.risk>0?"r":"g"}/>
          <MiniStat v={String(m.churn.risk)} l="Risiko-Patienten"/>
          <MiniStat v={String(m.churn.basis)} l="Basis (mit Besuchsdatum)"/>
        </div>
        {m.churn.list.length>0&&<>
          <SH t="Am längsten nicht gesehen"/>
          <Tbl heads={["Patient","Letzter Besuch","Vor","Status"]}>
            {m.churn.list.slice(0,8).map((r,i)=>(
              <tr key={i}>
                <Td bold>{r.name}</Td>
                <Td color="var(--mf-muted)">{r.last}</Td>
                <Td>{r.monate} Mon.</Td>
                <Td>{r.status&&<Sp color={r.status==="Inaktiv"?"r":"a"}>{r.status}</Sp>}</Td>
              </tr>
            ))}
          </Tbl>
        </>}
        <div style={{marginTop:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <Insight tone="a">Reaktivierungs-Mail über die Patientenliste (Zeile → Mail) oder Mail &amp; Newsletter → Einzelmail.</Insight>
          {onNavigate&&<Btn color="g" onClick={()=>onNavigate("patienten")}>Zur Patientenliste</Btn>}
        </div>
      </>
    ),
    hkp: () => !m.hkp.ok ? <EmptyState hint={m.hkp.hint}/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={m.hkp.quote!=null?`${m.hkp.quote} %`:"—"} l="Akzeptanzquote" tone="g"/>
          <MiniStat v={m.hkp.umsetzungsquote!=null?`${m.hkp.umsetzungsquote} %`:"—"} l="Umsetzungsquote"/>
          <MiniStat v={eur(m.hkp.realisiertVol)} l="Realisierter Umsatz" tone="g"/>
          <MiniStat v={String(m.hkp.imUmlauf)} l="HKP im Umlauf"/>
          <MiniStat v={eur(m.hkp.offenVol)} l="Offenes Volumen"/>
        </div>
        <SH t="Basis"/>
        <div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.6}}>
          {m.hkp.total} HKPs insgesamt · {m.hkp.entschieden} Patientenentscheidungen ({m.hkp.angenommen} angenommen)
          {m.hkp.avgDecisionDays!=null&&<> · Ø Entscheidungsdauer <strong style={{color:"var(--mf-text)"}}>{m.hkp.avgDecisionDays} Tage</strong></>}
        </div>
        <div style={{marginTop:10}}><Insight>Vollständige Pipeline mit Aktionen im Tab <strong>„HKP-Manager"</strong>.</Insight></div>
      </>
    ),
    umsatzart: () => !m.umsatz.ok ? <EmptyState hint={m.umsatz.hint} onAction={importAction} actionLabel="Zum Import"/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={m.umsatz.fmt} l={`Umsatz ${m.umsatz.latest.periode}`} tone="g"/>
          <MiniStat v={m.umsatz.deltaPct!=null?`${m.umsatz.deltaPct>=0?"+":""}${m.umsatz.deltaPct} %`:"—"} l="vs. Vorperiode" tone={m.umsatz.deltaPct>=0?"g":"r"}/>
          <MiniStat v={String(m.umsatz.trend.length)} l="Perioden importiert"/>
        </div>
        {m.umsatz.trend.length>=2&&<>
          <SH t="Umsatzverlauf (importierte Perioden)"/>
          <LineChart data={m.umsatz.trend.map(t=>t.umsatz)} color="#0F6E56" xL={m.umsatz.trend[0].periode} xR={m.umsatz.latest.periode}/>
        </>}
        <SH t="Perioden"/>
        <Tbl heads={["Periode","Umsatz","Neupatienten","Privatanteil"]}>
          {m.umsatz.trend.map(t=>(
            <tr key={t.periode}>
              <Td bold>{t.periode}</Td>
              <Td>{eur(t.umsatz)}</Td>
              <Td>{t.neupatienten??"—"}</Td>
              <Td>{t.privatanteil!=null?`${t.privatanteil} %`:"—"}</Td>
            </tr>
          ))}
        </Tbl>
        <div style={{marginTop:10}}><Insight tone="a">Die Aufschlüsselung nach <strong>Behandlungsart</strong> folgt, sobald Abrechnungsdaten als Quelle angebunden sind.</Insight></div>
      </>
    ),
    leistungen: () => !m.leistungen.ok ? <EmptyState hint={m.leistungen.hint} onAction={importAction} actionLabel="Zum Import"/> : (() => {
      const L = m.leistungen;
      // Nach Honorar sortieren, wo Beträge da sind — sonst nach Häufigkeit.
      // Ohne Honorarspalte wäre eine "Top-Umsatzträger"-Liste geraten.
      const top = L.mitBetrag ? L.topBetrag : L.topAnzahl;
      const maxBetrag = Math.max(...top.map(z=>L.mitBetrag?z.betrag:z.anzahl), 1);
      return (
        <>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
            <MiniStat v={String(L.zifferCount)} l="Verschiedene Ziffern"/>
            <MiniStat v={String(L.gesamtAnzahl)} l="Abrechnungen gesamt"/>
            {L.mitBetrag&&<MiniStat v={eur(Math.round(L.gesamtBetrag))} l="Honorar gesamt" tone="g"/>}
            {L.gozAnteil!=null&&<MiniStat v={`${L.gozAnteil} %`} l="GOZ-Anteil am Honorar" tone="g"/>}
            {L.schnitt!=null&&<MiniStat v={eur(Math.round(L.schnitt))} l="Ø je Abrechnung"/>}
          </div>
          <SH t={L.mitBetrag?"Größte Honorarträger":"Häufigste Ziffern"}/>
          {top.map(z=>(
            <HBar key={`${z.katalog}${z.ziffer}`} label={`${z.katalog?`${z.katalog} `:""}${z.ziffer}`}
              value={L.mitBetrag?z.betrag:z.anzahl} max={maxBetrag}
              color={z.katalog==="GOZ"?"#7C3AED":"#185FA5"}
              valStr={L.mitBetrag?`${eur(Math.round(z.betrag))} · ${z.anzahl}×`:`${z.anzahl}×`}/>
          ))}
          {top.some(z=>z.bezeichnung)&&(
            <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:6,lineHeight:1.6}}>
              {top.filter(z=>z.bezeichnung).slice(0,5).map(z=>`${z.ziffer} = ${z.bezeichnung}`).join(" · ")}
            </div>
          )}
          <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:6}}>
            {L.gozAnteil!=null&&(
              <Insight tone={L.gozAnteil>=30?"g":"a"}>
                <strong>{L.gozAnteil} %</strong> des Honorars kommen aus GOZ-Ziffern{m.kassenmix.ok?<>, bei <strong>{m.kassenmix.privatPct} %</strong> Privatpatienten im Stamm</>:null}. Die GOZ-Quote ist die belastbarere Zahl — sie zählt Leistungen, nicht Versichertenkarten.
              </Insight>
            )}
            {!L.mitBetrag&&(
              <Insight tone="a">Die Datei enthielt keine Honorarspalte — deshalb nur Häufigkeiten. Mit einer Spalte „Honorar“ oder „Betrag“ zeigt dida auch, welche Ziffern den Umsatz tragen.</Insight>
            )}
            <Insight>Datenstand: {L.perioden.length===1?`Periode ${L.perioden[0]}`:`${L.perioden.length} Perioden (${L.perioden[0]} – ${L.perioden[L.perioden.length-1]})`}. Neue Perioden kommen über den PVS-Import dazu.</Insight>
          </div>
        </>
      );
    })(),
    kassenmix: () => !m.kassenmix.ok ? <EmptyState hint={m.kassenmix.hint} onAction={importAction} actionLabel="Patienten importieren"/> : (
      <>
        <SH t="Patientenanteil nach Kostenträger"/>
        {m.kassenmix.verteilung.map((k,i)=>(
          <HBar key={k.kv} label={k.kv} value={k.count} max={m.kassenmix.verteilung[0].count} color={["#185FA5","#0F6E56","#1D9E75","#BA7517","#7C3AED","#9d9daa"][i%6]} valStr={`${k.pct} % (${k.count})`}/>
        ))}
        {m.kassenmix.umsatzPrivat!=null
          ? <Insight><strong>{m.kassenmix.privatPct} % Privatpatienten</strong> erwirtschaften laut Import <strong>{m.kassenmix.umsatzPrivat} %</strong> des Umsatzes.</Insight>
          : <Insight tone="a">Der <strong>Umsatzanteil</strong> je Kostenträger erscheint, sobald die Umsatzstatistik mit Privatanteil importiert wird.</Insight>}
      </>
    ),
    clv: () => !m.umsProPatient.ok ? <EmptyState hint={m.umsProPatient.hint} onAction={importAction} actionLabel="Zum Import"/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={m.umsProPatient.fmt} l={`Ø Umsatz je Patient (${m.umsProPatient.periode})`} tone="g"/>
          <MiniStat v={String(m.umsProPatient.aktive)} l="Aktive Patienten"/>
          <MiniStat v={m.umsatz.fmt} l="Periodenumsatz"/>
        </div>
        <Insight tone="a">Ein echter <strong>CLV</strong> (Patientenwert über die gesamte Behandlungsdauer) entsteht, sobald mehrere Monate Umsatzhistorie importiert sind.</Insight>
      </>
    ),
    noshow: () => !m.ausfall.ok ? <EmptyState hint={m.ausfall.hint}/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={m.ausfall.fmt} l="Ausfallquote (30 Tage)" tone="r"/>
          <MiniStat v={String(m.ausfall.abgesagt)} l="Abgesagte Termine"/>
          <MiniStat v={String(m.ausfall.gesamt)} l="Termine gesamt (30 T.)"/>
        </div>
        <Insight tone="a">Erfasst werden aktuell <strong>Absagen</strong> aus dem Terminplan. Die Unterscheidung „unentschuldigt erschienen nicht" (echter No-Show) folgt mit der Check-in-Erfassung.</Insight>
      </>
    ),
    recall: () => !m.recall.ok ? <EmptyState hint={m.recall.hint} onAction={importAction} actionLabel="Recall-Liste importieren"/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={String(m.recall.offen)} l="Offen" tone="a"/>
          <MiniStat v={String(m.recall.ueberfaellig)} l="Überfällig (> 60 T.)" tone="r"/>
          <MiniStat v={String(m.recall.kontaktiert)} l="Kontaktiert"/>
          <MiniStat v={String(m.recall.gebucht)} l="Gebucht" tone="g"/>
        </div>
        {m.recall.ruecklauf!=null
          ? <Insight><strong>{m.recall.ruecklauf} %</strong> der kontaktierten Patienten haben gebucht.</Insight>
          : <Insight tone="a">Die Rücklaufquote erscheint nach den ersten Recall-Versendungen (Tab „Recall").</Insight>}
      </>
    ),
    score: () => <ScoreBody/>,
    demografie: () => !m.demografie.ok ? <EmptyState hint={m.demografie.hint} onAction={importAction} actionLabel="Patienten importieren"/> : <AgeBody d={m.demografie}/>,
    auslastung: () => !m.auslastung.ok ? <EmptyState hint={m.auslastung.hint}/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={`${m.auslastung.pct} %`} l="Auslastung diese Woche" tone="g"/>
          <MiniStat v={`${Math.round(m.auslastung.gebuchtMin/60)} Std.`} l="Gebuchte Zeit"/>
          <MiniStat v={String(m.auslastung.termine)} l="Termine (Mo–Fr)"/>
        </div>
        <SH t="Pro Behandler (Anteil der Wochenkapazität)"/>
        {m.auslastung.beh.map(b=><HBar key={b.id} label={b.name} value={b.pct} max={100} color={b.pct>=80?"#0F6E56":b.pct>=50?"#185FA5":"#BA7517"} valStr={`${b.pct} %`}/>)}
        <Insight tone="a">Kapazität aus den Öffnungszeiten des Terminplans (Einstellungen im Kalender). Historische Auslastungs-Trends entstehen mit der Nutzungsdauer.</Insight>
      </>
    ),
    behandler: () => !m.auslastung.ok ? <EmptyState hint={m.auslastung.hint}/> : (
      <>
        <SH t="Auslastung je Behandler (aktuelle Woche)"/>
        {m.auslastung.beh.map(b=><HBar key={b.id} label={b.name} value={b.pct} max={100} color="#0F6E56" valStr={`${b.pct} %`}/>)}
        <Insight tone="a"><strong>Umsatz und Bewertung je Behandler</strong> folgen, sobald Abrechnungsdaten bzw. Bewertungs-Zuordnungen als Quellen angebunden sind.</Insight>
      </>
    ),
    feedback: () => !m.feedback.ok ? <EmptyState hint={m.feedback.hint}/> : (
      <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:7,marginBottom:12}}>
          <MiniStat v={`${String(m.feedback.avg).replace(".",",")} ★`} l="Ø Bewertung" tone="g"/>
          <MiniStat v={String(m.feedback.count)} l="Bewertungen gesamt"/>
        </div>
        <SH t="Verteilung"/>
        {m.feedback.dist.map(d=><HBar key={d.stars} label={`${d.stars} Sterne`} value={d.count} max={Math.max(...m.feedback.dist.map(x=>x.count),1)} color="#BA7517" valStr={String(d.count)}/>)}
        <Insight>Details und Antworten im Modul <strong>Bewertungen</strong>. Feedback je Behandler folgt mit der Behandler-Zuordnung von Bewertungen.</Insight>
      </>
    ),
  }[kpi];

  return body?body():null;
}

// Altersverteilung: geordnete Balken statt Tortendiagramm — Gruppen lassen
// sich so der Größe nach vergleichen, jeder Balken trägt Label und Wert
// (Identität hängt nie allein an der Farbe).
function AgeBody({ d }) {
  const ramp = useAgeRamp();
  const max = Math.max(...d.groups.map(g=>g.count), 1);
  return (
    <>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:7,marginBottom:12}}>
        <MiniStat v={`${d.avg} J.`} l="Ø Alter"/>
        <MiniStat v={`${d.median} J.`} l="Median"/>
        <MiniStat v={d.groesste.label.replace(" Jahre","")} l={`Größte Gruppe (${d.groesste.pct} %)`}/>
        <MiniStat v={String(d.basis)} l="Mit Geburtsdatum"/>
      </div>
      <SH t="Verteilung nach Altersgruppe"/>
      {d.groups.map((g,i)=>(
        <HBar key={g.key} label={g.label} value={g.count} max={max} color={ramp[i]} valStr={`${g.pct} % (${g.count})`}/>
      ))}
      <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:10}}>
        {d.seniorPct>=30&&<Insight tone="a"><strong>{d.seniorPct} %</strong> deiner Patienten sind 65 oder älter — in dieser Gruppe fallen Zahnersatz, Parodontitis-Behandlung und aufsuchende Betreuung am häufigsten an.</Insight>}
        {d.kinderPct<10&&<Insight tone="a">Nur <strong>{d.kinderPct} %</strong> sind unter 18 — ohne nachwachsende Jahrgänge altert der Stamm mit der Praxis. Kinderprophylaxe und Familientermine sind der übliche Hebel.</Insight>}
        {d.ohneDob>0&&<Insight>Für <strong>{d.ohneDob}</strong> von {d.total} Patienten fehlt ein auswertbares Geburtsdatum (Format TT.MM.JJJJ) — sie fließen nicht in die Verteilung ein.</Insight>}
      </div>
    </>
  );
}

function scoreCounts(patients) {
  const counts = { A:0,B:0,C:0,D:0 };
  patients.forEach(p=>{counts[scoreMeta(p.score).grade]++;});
  const avg = patients.length?Math.round(patients.reduce((s,p)=>s+p.score,0)/patients.length):0;
  return { counts, avg };
}

function ScoreBody() {
  const patients = useMemo(()=>loadPatientsCache().map(p=>({...p,score:computeScore(p)})),[]);
  const { counts } = scoreCounts(patients);
  const worst = [...patients].sort((a,b)=>a.score-b.score).slice(0,3);
  return (
    <>
      <SH t="Segmentverteilung"/>
      {["A","B","C","D"].map(g=>{
        const m = scoreMeta(g==="A"?90:g==="B"?70:g==="C"?50:20);
        return <HBar key={g} label={`${g} · ${m.label}`} value={counts[g]} max={patients.length} color={m.bar} valStr={`${counts[g]} Pat.`}/>;
      })}
      <SH t="Niedrigste Scores"/>
      {worst.map(p=>(
        <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
          <span style={{fontSize:11,fontWeight:500,color:"var(--mf-text)"}}>{p.nachname}, {p.vorname}</span>
          <ScoreBadge score={p.score}/>
        </div>
      ))}
      <div style={{marginTop:8}}>
        <Insight>Vollständige Scorecard mit Faktoren-Aufschlüsselung und Handlungsempfehlungen im Tab <strong>„Scorecard“</strong>.</Insight>
      </div>
    </>
  );
}

function KpiDetail({ kpi, m, tile, onClose, onNavigate }) {
  const meta = KPI_META[kpi];
  return (
    <Overlay title={meta.l} sub={tile.empty?"Keine Datenquelle":`${tile.v}${tile.d?` · ${tile.d}`:""}`} icon={meta.icon} onClose={onClose}>
      {KPI_INFO[kpi]&&<div style={{fontSize:11,color:"var(--mf-muted)",lineHeight:1.5,marginBottom:12,paddingBottom:10,borderBottom:"0.5px solid var(--mf-border)"}}>{KPI_INFO[kpi]}</div>}
      <KpiBody kpi={kpi} m={m} onNavigate={onNavigate}/>
    </Overlay>
  );
}

function HkpTab() {
  // Cache sofort anzeigen, dann frisch laden (Supabase im LIVE-Modus)
  const [hkps, setHkps] = useState(loadHkpsCache);
  useEffect(()=>{ let on=true; listHkps().then(r=>{ if(on) setHkps(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  const [statusF, setStatusF] = useState("");
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showArchiv, setShowArchiv] = useState(false);
  const [editId, setEditId] = useState(null); // HKP-Id im Bearbeiten-Modus
  const [newHkp, setNewHkp] = useState({patient:"",leistung:"",wert:"",behandler:getBehandler()[0]?.name||"",art:"gkv"});

  const heute = () => new Date().toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"});
  const setStatus = (id,status) => {
    const today = heute();
    const patch = { status,
      ...(status==="vorgelegt"?{vorgelegt:today}:{}),
      ...(status==="eingereicht"?{eingereicht:today}:{}),
      ...(status==="genehmigt"?{genehmigtAm:today}:{}),
      ...(["angenommen","abgelehnt"].includes(status)?{entschieden:today}:{}),
      ...(status==="durchgefuehrt"?{durchgefuehrtAm:today}:{}) };
    setHkps(p=>p.map(h=>h.id===id?{...h,...patch}:h)); // optimistisch
    updateHkp(id, patch).then(setHkps).catch(()=>toast("Statuswechsel nicht gespeichert — Verbindung prüfen.","r"));
    setSel(null);
  };
  // Nachfass-Mail an den Patienten (LIVE, wenn E-Mail hinterlegt)
  const sendNachfass = async (h) => {
    if(!serverActive()) return;
    const email = findPatientEmail(h.patient);
    if(!email){ toast("Keine E-Mail hinterlegt — Nachfassen nur intern vermerkt.","a"); return; }
    try{
      await sendMails([{ to:email, toName:h.patient,
        subject:"Ihr Behandlungsplan — haben Sie noch Fragen?",
        text:`Liebe/r ${h.patient.split(",")[1]?.trim()||h.patient},\n\nwir möchten freundlich an Ihren Heil- und Kostenplan erinnern (${h.leistung}). Falls Sie noch Fragen zu Behandlung, Kosten oder Alternativen haben, beraten wir Sie gerne — telefonisch oder bei einem kurzen Termin.\n\nMit freundlichen Grüßen\n${praxisAbsender()}` }]);
      toast("Nachfass-Mail gesendet ✓");
    }catch(e){ toast(`Mail fehlgeschlagen: ${e.message}`,"r"); }
  };
  const nachfassen = (id) => {
    const patch = { nachgefasst: heute() };
    setHkps(p=>p.map(h=>h.id===id?{...h,...patch}:h));
    updateHkp(id, patch).then(setHkps).catch(()=>toast("Nachfassen nicht gespeichert — Verbindung prüfen.","r"));
  };
  const addHkp = () => {
    if(!newHkp.patient||!newHkp.leistung||!newHkp.wert) return;
    if(!newHkp.behandler||newHkp.behandler==="—"){ toast("Zuerst Behandler anlegen (Benutzermenü → Praxis & Behandler).","r"); return; }
    const felder = {patient:newHkp.patient,leistung:newHkp.leistung,wert:parseInt(newHkp.wert)||0,behandler:newHkp.behandler,art:newHkp.art};
    if (editId) {
      setHkps(p=>p.map(h=>h.id===editId?{...h,...felder}:h)); // optimistisch
      updateHkp(editId, felder).then(setHkps).catch(()=>toast("Änderung nicht gespeichert — Verbindung prüfen.","r"));
    } else {
      storeAddHkp({...felder,status:"entwurf",erstellt:heute()}).then(setHkps).catch(()=>toast("HKP konnte nicht angelegt werden — Verbindung prüfen.","r"));
    }
    setShowNew(false); setEditId(null);
    setNewHkp({patient:"",leistung:"",wert:"",behandler:getBehandler()[0]?.name||"",art:"gkv"});
  };
  const startEdit = (h) => {
    setNewHkp({patient:h.patient,leistung:h.leistung,wert:String(h.wert),behandler:h.behandler,art:h.art||"gkv"});
    setEditId(h.id); setSel(null); setShowNew(true);
  };
  const delHkp = (id) => {
    if(!window.confirm("Diesen HKP endgültig löschen? Er fließt dann nicht mehr in die Kennzahlen ein — zum Aufräumen besser archivieren.")) return;
    setHkps(p=>p.filter(h=>h.id!==id)); setSel(null);
    storeDeleteHkp(id).then(setHkps).catch(()=>{ toast("Löschen fehlgeschlagen — Verbindung prüfen.","r"); listHkps().then(setHkps).catch(()=>{}); });
  };
  // Archiv = reine Anzeige-Eigenschaft: Zeile bleibt erhalten und zählt
  // weiter in Akzeptanzquote & Co., verschwindet nur aus der Alltagsliste.
  const setArchiv = (id, val) => {
    setHkps(p=>p.map(h=>h.id===id?{...h,archiviert:val}:h)); setSel(null);
    updateHkp(id,{archiviert:val}).then(setHkps).catch(()=>toast("Nicht gespeichert — Verbindung prüfen. (Fehlt schema_v5_hkp_art.sql?)","r"));
  };
  const DURCH = ["durchgefuehrt","abgelehnt","abgelaufen"]; // abgeschlossen = archivierbar
  const archiveDone = async () => {
    const done = hkps.filter(h=>!h.archiviert&&DURCH.includes(h.status));
    if(!done.length) return;
    setHkps(p=>p.map(h=>DURCH.includes(h.status)?{...h,archiviert:true}:h));
    try {
      for (const d of done) await updateHkp(d.id,{archiviert:true});
      toast(`${done.length} abgeschlossene HKP archiviert`);
    } catch {
      toast("Archivieren unvollständig — Verbindung prüfen. (Fehlt schema_v5_hkp_art.sql?)","r");
      listHkps().then(setHkps).catch(()=>{});
    }
  };

  // Akzeptanzquote: nur echte Patientenentscheidungen (abgelaufene HKPs separat)
  const ACCEPTED = ["angenommen","durchgefuehrt"];
  const entschiedene = hkps.filter(h=>[...ACCEPTED,"abgelehnt"].includes(h.status));
  const quote = entschiedene.length?Math.round(hkps.filter(h=>ACCEPTED.includes(h.status)).length/entschiedene.length*100):0;
  const IM_UMLAUF = ["vorgelegt","eingereicht","genehmigt"];
  const offenVol = hkps.filter(h=>IM_UMLAUF.includes(h.status)).reduce((s,h)=>s+h.wert,0);
  const entwurfVol = hkps.filter(h=>h.status==="entwurf").reduce((s,h)=>s+h.wert,0);
  // Kacheln & Quote rechnen bewusst über ALLE HKPs (inkl. Archiv) —
  // nur die Liste unten blendet Archiviertes aus.
  const aktiv = hkps.filter(h=>!h.archiviert);
  const archiv = hkps.filter(h=>h.archiviert);
  const basis = showArchiv ? archiv : aktiv;
  const filtered = statusF ? basis.filter(h=>h.status===statusF) : basis;
  const doneCount = aktiv.filter(h=>DURCH.includes(h.status)).length;
  const T = useTones();

  // Pipeline-Stufen (nur aktive, nicht archivierte HKPs)
  const STAGES = [
    { key:"entwurf",      label:"Entwurf",      tone:"n"  },
    { key:"vorgelegt",    label:"Vorgelegt",    tone:"b"  },
    { key:"eingereicht",  label:"Bei Kasse",    tone:"pu" },
    { key:"genehmigt",    label:"Genehmigt",    tone:"b"  },
    { key:"angenommen",   label:"Angenommen",   tone:"g"  },
    { key:"durchgefuehrt",label:"Durchgeführt", tone:"g"  },
  ];
  const stageData = STAGES.map(s=>{ const list=aktiv.filter(h=>h.status===s.key); return {...s,count:list.length,value:list.length?eur(list.reduce((a,h)=>a+h.wert,0)):"—"}; });
  const realisiert = hkps.filter(h=>h.status==="durchgefuehrt").reduce((s,h)=>s+h.wert,0);
  const umsetzung = (()=>{const a=hkps.filter(h=>ACCEPTED.includes(h.status)).length;const d=hkps.filter(h=>h.status==="durchgefuehrt").length;return a?Math.round(d/a*100):null;})();
  const dauer = (()=>{const s=hkps.filter(h=>h.vorgelegt&&h.entschieden).map(h=>{try{const p=d=>{const[dd,mm,yy]=d.split(".");return new Date(2000+ +yy,+mm-1,+dd);};return (p(h.entschieden)-p(h.vorgelegt))/86400000;}catch{return null;}}).filter(x=>x!=null&&x>=0);return s.length?(Math.round(s.reduce((a,b)=>a+b,0)/s.length*10)/10).toLocaleString("de-DE"):null;})();
  // Wie viele Pläne warten schon lange auf die Patientenentscheidung?
  const wartet = aktiv.filter(h=>["vorgelegt","genehmigt"].includes(h.status)&&(()=>{const d=daysSince(h.vorgelegt);return d!=null&&d>14;})()).length;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        <StatTile tone="g" v={`${quote} %`} l="Akzeptanzquote" d="angenommen / entschieden" active={statusF==="angenommen"} onClick={()=>setStatusF(statusF==="angenommen"?"":"angenommen")}/>
        <StatTile tone="b" v={eur(offenVol)} l="Offenes Volumen" d={`${hkps.filter(h=>IM_UMLAUF.includes(h.status)).length} HKP im Umlauf`} active={statusF==="vorgelegt"} onClick={()=>setStatusF(statusF==="vorgelegt"?"":"vorgelegt")}/>
        <StatTile tone="n" v={eur(entwurfVol)} l="In Vorbereitung" d={`${hkps.filter(h=>h.status==="entwurf").length} Entwürfe`} active={statusF==="entwurf"} onClick={()=>setStatusF(statusF==="entwurf"?"":"entwurf")}/>
        <StatTile tone="g" v={umsetzung!=null?`${umsetzung} %`:"—"} l="Umsetzungsquote" d="durchgeführt / angenommen" active={statusF==="durchgefuehrt"} onClick={()=>setStatusF(statusF==="durchgefuehrt"?"":"durchgefuehrt")}/>
        <StatTile tone="g" v={eur(realisiert)} l="Realisierter Umsatz" d={`${hkps.filter(h=>h.status==="angenommen").length} angenommen noch offen`} active={statusF==="angenommen"} onClick={()=>setStatusF(statusF==="angenommen"?"":"angenommen")}/>
        <StatTile tone="n" v={dauer!=null?`${dauer} Tage`:"—"} l="Ø Entscheidungsdauer" d="Vorlage → Entscheidung" onClick={()=>setStatusF("")}/>
      </div>

      <Card>
        <CardHead title="HKP-Pipeline" icon="ti-git-branch" right={<span style={{fontSize:10,color:"var(--mf-muted)"}}>Klick filtert die Liste</span>}/>
        <PipelineBar stages={stageData} activeKey={showArchiv?"":statusF} onPick={k=>{setShowArchiv(false);setStatusF(k);}}/>
        {wartet>0&&<div style={{marginTop:10}}><Insight tone="a"><strong>{wartet}</strong> {wartet===1?"Plan wartet":"Pläne warten"} seit über 14 Tagen auf die Patientenentscheidung — Nachfassen lohnt.</Insight></div>}
      </Card>

      <Card>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
          <FilterPill active={!statusF&&!showArchiv} onClick={()=>{setStatusF("");setShowArchiv(false);}}>Alle ({aktiv.length})</FilterPill>
          {["abgelehnt","abgelaufen"].map(k=>(
            <FilterPill key={k} active={statusF===k} onClick={()=>{setShowArchiv(false);setStatusF(statusF===k?"":k);}}>{HKP_STATUS[k].l} ({aktiv.filter(h=>h.status===k).length})</FilterPill>
          ))}
          <FilterPill active={showArchiv} onClick={()=>{setShowArchiv(v=>!v);setStatusF("");}}>Archiv ({archiv.length})</FilterPill>
          <div style={{flex:1}}/>
          {doneCount>0&&!showArchiv&&<Btn onClick={archiveDone}>Abgeschlossene archivieren ({doneCount})</Btn>}
          <Btn color="g" onClick={()=>setShowNew(true)}>+ Neuer HKP</Btn>
        </div>
        <Tbl heads={["Patient","Leistung","Wert","Behandler","Status","Vorgelegt",""]}>
          {filtered.map(h=>(
            <tr key={h.id} onClick={()=>setSel(h)} style={{cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Td bold style={{borderLeft:`3px solid ${T[HKP_STATUS[h.status].color].bar}`,paddingLeft:9}}>{h.patient}</Td>
              <Td style={{maxWidth:170,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.leistung}{h.art==="privat"&&<Sp color="pu" style={{marginLeft:5}}>Privat</Sp>}</Td>
              <Td bold>{eur(h.wert)}</Td>
              <Td color="var(--mf-muted)">{h.behandler}</Td>
              <Td><Sp color={HKP_STATUS[h.status].color}>{HKP_STATUS[h.status].l}</Sp></Td>
              <Td color="var(--mf-muted)">{h.vorgelegt||"—"}</Td>
              <Td>{h.nachgefasst&&<span style={{fontSize:9,color:"var(--mf-primary)"}}>✓ erinnert {h.nachgefasst}</span>}</Td>
            </tr>
          ))}
        </Tbl>
        {filtered.length===0&&<div style={{padding:"14px 0",fontSize:11,color:"var(--mf-muted)",textAlign:"center"}}>{showArchiv?"Archiv ist leer.":"Keine HKP in diesem Status."}</div>}
        {showArchiv&&archiv.length>0&&<div style={{fontSize:10,color:"var(--mf-muted)",marginTop:6}}>Archivierte HKPs zählen weiterhin in Akzeptanzquote und Kennzahlen — sie sind nur aus der Alltagsliste ausgeblendet.</div>}
      </Card>

      {sel&&(
        <Overlay title={sel.patient} sub={`${sel.leistung} · ${eur(sel.wert)}`} icon="ti-file-check" onClose={()=>setSel(null)} width={460}>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {[["Status",<Sp color={HKP_STATUS[sel.status].color}>{HKP_STATUS[sel.status].l}</Sp>],["Abrechnungsart",sel.art==="privat"?"Privat / MKV":"Gesetzlich (GKV)"],["Behandler",sel.behandler],["Erstellt",sel.erstellt],["Patient vorgelegt",sel.vorgelegt||"—"],
              ...(sel.art==="privat"?[]:[["Bei Kasse eingereicht",sel.eingereicht||"—"],["Kasse genehmigt",sel.genehmigtAm||"—"]]),
              ["Patientenentscheidung",sel.entschieden||"—"],
              ...(["angenommen","durchgefuehrt"].includes(sel.status)?[["Durchgeführt",sel.durchgefuehrtAm||"— noch offen"]]:[])].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"4px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
                <span style={{color:"var(--mf-muted)"}}>{l}</span><span style={{color:"var(--mf-text)"}}>{v}</span>
              </div>
            ))}
            {sel.grund&&<Insight tone="r"><strong>Ablehnungsgrund:</strong> {sel.grund}</Insight>}
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:4}}>
              {sel.status==="entwurf"&&<Btn color="g" onClick={()=>setStatus(sel.id,"vorgelegt")}>Patienten vorlegen</Btn>}
              {sel.status==="vorgelegt"&&<>
                {sel.art==="privat"
                  ? <>
                      <Btn color="g" onClick={()=>setStatus(sel.id,"angenommen")}>Patient nimmt an</Btn>
                      <Btn color="r" onClick={()=>setStatus(sel.id,"abgelehnt")}>Patient lehnt ab</Btn>
                    </>
                  : <Btn color="g" onClick={()=>setStatus(sel.id,"eingereicht")}>Bei Kasse einreichen</Btn>}
                <Btn onClick={()=>{const d=new Date().toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"});nachfassen(sel.id);setSel(s=>({...s,nachgefasst:d}));sendNachfass(sel);}}>{sel.nachgefasst?"✓ Nachgefasst":"Nachfassen (E-Mail)"}</Btn>
              </>}
              {sel.status==="eingereicht"&&<>
                <Btn color="g" onClick={()=>setStatus(sel.id,"genehmigt")}>Kasse hat genehmigt</Btn>
                <Btn color="r" onClick={()=>setStatus(sel.id,"abgelehnt")}>Kasse lehnt ab</Btn>
              </>}
              {sel.status==="genehmigt"&&<>
                <Btn color="g" onClick={()=>setStatus(sel.id,"angenommen")}>Patient nimmt an</Btn>
                <Btn color="r" onClick={()=>setStatus(sel.id,"abgelehnt")}>Patient lehnt ab</Btn>
                <Btn onClick={()=>{const d=new Date().toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"});nachfassen(sel.id);setSel(s=>({...s,nachgefasst:d}));sendNachfass(sel);}}>{sel.nachgefasst?"✓ Nachgefasst":"Nachfassen (E-Mail)"}</Btn>
              </>}
              {sel.status==="angenommen"&&<Btn color="g" onClick={()=>setStatus(sel.id,"durchgefuehrt")}>Behandlung durchgeführt</Btn>}
              {sel.status==="abgelehnt"&&<Btn onClick={()=>setStatus(sel.id,"entwurf")}>Neu kalkulieren</Btn>}
              {sel.status==="abgelaufen"&&<Btn color="g" onClick={()=>setStatus(sel.id,"vorgelegt")}>Reaktivieren & erneut vorlegen</Btn>}
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:2,paddingTop:8,borderTop:"0.5px solid var(--mf-border)"}}>
              <Btn onClick={()=>startEdit(sel)}>Bearbeiten</Btn>
              {!sel.archiviert&&DURCH.includes(sel.status)&&<Btn onClick={()=>setArchiv(sel.id,true)}>Archivieren</Btn>}
              {sel.archiviert&&<Btn onClick={()=>setArchiv(sel.id,false)}>Aus Archiv holen</Btn>}
              <div style={{flex:1}}/>
              <Btn color="r" onClick={()=>delHkp(sel.id)}>Löschen</Btn>
            </div>
          </div>
        </Overlay>
      )}

      {showNew&&(
        <Overlay title={editId?"HKP bearbeiten":"Neuen HKP anlegen"} icon={editId?"ti-pencil":"ti-file-plus"} onClose={()=>{setShowNew(false);setEditId(null);}} width={420}>
          <div style={{display:"flex",flexDirection:"column"}}>
            <PatientPicker label="Patient" value={newHkp.patient} onChange={v=>setNewHkp(p=>({...p,patient:v}))} onPick={pt=>setNewHkp(p=>({...p,patient:`${pt.nachname}, ${pt.vorname}`}))}/>
            <Input label="Leistung" value={newHkp.leistung} onChange={v=>setNewHkp(p=>({...p,leistung:v}))} placeholder="z.B. Krone 46 (Zirkon)"/>
            <Input label="Wert in €" value={newHkp.wert} onChange={v=>setNewHkp(p=>({...p,wert:v.replace(/\D/g,"")}))} placeholder="z.B. 1240"/>
            <Select label="Behandler" value={newHkp.behandler} onChange={v=>setNewHkp(p=>({...p,behandler:v}))} options={getBehandler().length?getBehandler().map(b=>b.name):["—"]}/>
            <Select label="Abrechnungsart" value={newHkp.art} onChange={v=>setNewHkp(p=>({...p,art:v}))} options={[{value:"gkv",label:"Gesetzlich (GKV) — über die Kasse"},{value:"privat",label:"Privat / MKV — ohne Kassenweg"}]}/>
            {newHkp.art==="privat"&&<div style={{fontSize:10.5,color:"var(--mf-muted)",marginTop:-4,marginBottom:6}}>Private Pläne und Mehrkostenvereinbarungen überspringen „Bei Kasse einreichen“ — nach dem Vorlegen entscheidet der Patient direkt.</div>}
            <div style={{display:"flex",gap:10,marginTop:4}}>
              <Btn color="g" lg style={{flex:1}} onClick={addHkp}>{editId?"Änderungen speichern":"Als Entwurf anlegen"}</Btn>
              <Btn lg onClick={()=>{setShowNew(false);setEditId(null);}}>Abbrechen</Btn>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: SELEKTIVER RECALL
// ═══════════════════════════════════════════════════════════════════════════

function RecallTab() {
  // Cache sofort anzeigen, dann frisch laden (Supabase im LIVE-Modus);
  // Fälligkeit wird im Store immer frisch aus lastPzr berechnet
  const [recalls, setRecalls] = useState(loadRecallsCache);
  useEffect(()=>{ let on=true; listRecalls().then(r=>{ if(on) setRecalls(r); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  const [filter, setFilter] = useState("offen");
  const [behF, setBehF] = useState("");
  const [checked, setChecked] = useState([]);
  const [kanal, setKanal] = useState("E-Mail");
  const [showNewRecall, setShowNewRecall] = useState(false);
  // Versandtext: was hier steht, wird gesendet — {name} wird je Patient ersetzt
  const [recallSubject, setRecallSubject] = useState("Zeit für Ihre Prophylaxe — jetzt Termin vereinbaren");
  const [recallText, setRecallText] = useState(() => `Liebe/r {name},\n\nIhre letzte professionelle Zahnreinigung liegt schon eine Weile zurück — Zeit für Ihren nächsten Prophylaxe-Termin. Regelmäßige Zahnreinigung ist der beste Schutz vor Karies und Parodontitis.\n\nRufen Sie uns einfach an oder antworten Sie auf diese E-Mail — wir finden gemeinsam einen passenden Termin.\n\nWir freuen uns auf Sie!\n\nMit freundlichen Grüßen\n${praxisAbsender()}`);
  const [newRecall, setNewRecall] = useState({name:"",dob:"",lastPzr:"",intervalTage:182,behandler:getBehandler()[0]?.name||""});
  // Beim Öffnen: Recall-Kandidaten automatisch aus Patientenstamm + PZR-Terminen übernehmen
  useEffect(()=>{ let on=true;
    listPatients().then(()=>syncRecallsFromPatients()).then(res=>{
      if(on&&res&&(res.added||res.updated)){
        toast(`Recall-Liste aktualisiert: ${res.added} neu${res.updated?`, ${res.updated} vordatiert`:""}`);
        listRecalls().then(r=>{ if(on) setRecalls(r); }).catch(()=>{});
      }
    }).catch(()=>{});
    return ()=>{ on=false; };
  },[]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sendOpen, setSendOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  const filtered = useMemo(()=>recalls.filter(r=>{
    if(filter==="offen"&&r.status!=="offen") return false;
    if(filter==="ueberfaellig"&&!(r.status==="offen"&&r.tage>60)) return false;
    if(filter==="kontaktiert"&&r.status!=="kontaktiert") return false;
    if(filter==="gebucht"&&r.status!=="gebucht") return false;
    if(behF&&r.behandler!==behF) return false;
    return true;
  }),[recalls,filter,behF]);
  const toggle = (id) => setChecked(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const offenFiltered = filtered.filter(r=>r.status==="offen");
  const allChecked = offenFiltered.length>0&&offenFiltered.every(r=>checked.includes(r.id));
  const toggleAll = () => setChecked(allChecked?[]:offenFiltered.map(r=>r.id));
  const send = async () => {
    const rows = recalls.filter(r=>checked.includes(r.id));
    setSentCount(checked.length);
    setRecalls(p=>p.map(r=>checked.includes(r.id)?{...r,status:"kontaktiert",kanalUsed:kanal}:r)); // optimistisch
    markKontaktiert(checked, kanal).then(setRecalls).catch(()=>toast("Recall-Status nicht gespeichert — Verbindung prüfen.","r"));
    setChecked([]);
    setSent(true);
    // Kanal E-Mail: im Live-Betrieb wirklich versenden (Mailjet)
    if(/mail/i.test(kanal) && serverActive()){
      const withMail = rows.map(r=>({r,email:findPatientEmail(r.name)})).filter(x=>x.email);
      const ohne = rows.length - withMail.length;
      if(!withMail.length){ toast("Keine E-Mail-Adressen hinterlegt — Status trotzdem auf „kontaktiert“ gesetzt.","a"); return; }
      try{
        let sent=0;
        for(let i=0;i<withMail.length;i+=50){
          const res=await sendMails(withMail.slice(i,i+50).map(({r,email})=>({
            to:email, toName:r.name,
            subject: recallSubject,
            text: recallText.replace(/\{name\}/g, r.name.split(",")[1]?.trim()||r.name),
          })));
          sent+=res.sent||0;
        }
        toast(`${sent} Recall-Mails gesendet${ohne?` · ${ohne} ohne E-Mail übersprungen`:""} ✓`);
      }catch(e){ toast(`Mail-Versand fehlgeschlagen: ${e.message}`,"r"); }
    }
  };
  const behandlerList = useMemo(()=>[...new Set(recalls.map(r=>r.behandler))],[recalls]);
  const offen = recalls.filter(r=>r.status==="offen");
  const ueberf = offen.filter(r=>r.tage>60);
  const T = useTones();
  const kontaktiertN = recalls.filter(r=>r.status==="kontaktiert").length;
  const gebuchtN = recalls.filter(r=>r.status==="gebucht").length;
  const ruecklauf = (kontaktiertN+gebuchtN)?Math.round(gebuchtN/(kontaktiertN+gebuchtN)*100):null;
  // Rücklauf-Trichter: der Weg vom fälligen Recall zum gebuchten Termin
  const recStages = [
    { key:"offen",       label:"Offen · fällig", tone:"a", count:offen.length },
    { key:"kontaktiert", label:"Kontaktiert",    tone:"b", count:kontaktiertN },
    { key:"gebucht",     label:"Termin gebucht", tone:"g", count:gebuchtN },
  ];
  // Ampel-Farbe je Zeile nach Dringlichkeit/Status
  const rowTone = (r) => r.status==="gebucht"?"g":r.status==="kontaktiert"?"b":r.tage>60?"r":"a";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        <StatTile tone="a" v={String(offen.length)} l="Fällige Recalls" d="noch nicht kontaktiert" active={filter==="offen"} onClick={()=>setFilter("offen")}/>
        <StatTile tone="r" v={String(ueberf.length)} l="Überfällig (> 60 Tage)" d="dringend kontaktieren" active={filter==="ueberfaellig"} onClick={()=>setFilter("ueberfaellig")}/>
        <StatTile tone="g" v={ruecklauf!=null?`${ruecklauf} %`:"—"} l="Rücklaufquote" d="kontaktiert → gebucht" active={filter==="gebucht"} onClick={()=>setFilter("gebucht")}/>
        <StatTile tone="g" v={String(gebuchtN)} l="Termin gebucht" d="aus Recall entstanden" active={filter==="kontaktiert"} onClick={()=>setFilter("kontaktiert")}/>
      </div>

      <Card>
        <CardHead title="Rücklauf-Trichter" icon="ti-filter" right={<span style={{fontSize:10,color:"var(--mf-muted)"}}>Klick filtert die Liste</span>}/>
        <PipelineBar stages={recStages} activeKey={["offen","kontaktiert","gebucht"].includes(filter)?filter:""} onPick={k=>{setFilter(k||"alle");setChecked([]);}}/>
        {ueberf.length>0&&<div style={{marginTop:10}}><Insight tone="r"><strong>{ueberf.length}</strong> {ueberf.length===1?"Patient ist":"Patienten sind"} seit über 60 Tagen überfällig — mit niedrigem Score zuerst persönlich kontaktieren.</Insight></div>}
      </Card>

      <Card>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
          {[["offen","Offen"],["ueberfaellig","Überfällig"],["kontaktiert","Kontaktiert"],["gebucht","Gebucht"],["alle","Alle"]].map(([k,l])=>(
            <FilterPill key={k} active={filter===k} onClick={()=>{setFilter(k);setChecked([]);}}>{l}</FilterPill>
          ))}
          <div style={{flex:1}}/>
          <select value={behF} onChange={e=>setBehF(e.target.value)} style={{fontSize:10,padding:"4px 7px",borderRadius:5,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit"}}>
            <option value="">Alle Behandler</option>
            {behandlerList.map(b=><option key={b}>{b}</option>)}
          </select>
          <Btn onClick={async()=>{ try{ const res=await syncRecallsFromPatients(); toast(res.added||res.updated?`${res.added} neu, ${res.updated} vordatiert`:"Alles aktuell — keine neuen Kandidaten."); listRecalls().then(setRecalls).catch(()=>{}); }catch{ toast("Übernahme fehlgeschlagen — Verbindung prüfen.","r"); } }}>Aus Patienten übernehmen</Btn>
          <Btn color="g" onClick={()=>setShowNewRecall(true)}>+ Recall anlegen</Btn>
        </div>

        <Tbl heads={["",`Patient`,"Letzte PZR","Fällig seit","Score","Behandler","Status"]}>
          {filtered.map(r=>(
            <tr key={r.id} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Td style={{width:24,borderLeft:`3px solid ${T[rowTone(r)].bar}`,paddingLeft:9}}>{r.status==="offen"&&<input type="checkbox" checked={checked.includes(r.id)} onChange={()=>toggle(r.id)} style={{cursor:"pointer"}}/>}</Td>
              <Td bold>{r.name}</Td>
              <Td color="var(--mf-muted)">{r.lastPzr}</Td>
              <Td color={r.tage>60?"var(--mf-neg)":"var(--mf-text)"} bold={r.tage>60}>{r.tage} Tagen</Td>
              <Td><ScoreBadge score={r.score}/></Td>
              <Td color="var(--mf-muted)">{r.behandler}</Td>
              <Td><Sp color={{offen:"a",kontaktiert:"b",gebucht:"g"}[r.status]}>{ {offen:"Offen",kontaktiert:`Kontaktiert${r.kanalUsed?` (${r.kanalUsed})`:""}`,gebucht:"Termin gebucht"}[r.status] }</Sp>
                {r.status!=="offen"&&<span title="Zurück auf Offen" onClick={()=>{ setRecalls(p=>p.map(x=>x.id===r.id?{...x,status:"offen",kanalUsed:undefined}:x)); setRecallStatus(r.id,"offen").then(setRecalls).catch(()=>toast("Nicht gespeichert — Verbindung prüfen.","r")); }}
                  style={{marginLeft:6,cursor:"pointer",fontSize:11,color:"var(--mf-muted)"}}>↺</span>}</Td>
            </tr>
          ))}
        </Tbl>
        {filtered.length===0&&<div style={{padding:"14px 0",fontSize:11,color:"var(--mf-muted)",textAlign:"center",lineHeight:1.6}}>{recalls.length===0
          ? <>Noch keine Recalls. <strong>„Aus Patienten übernehmen“</strong> erstellt Einträge aus deinem Patientenstamm (PZR-Termine &amp; letzte Besuche) — oder lege einzeln über <strong>„+ Recall anlegen“</strong> an.</>
          : "Keine Patienten in dieser Ansicht."}</div>}

        {filtered.some(r=>r.status==="offen")&&(
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,paddingTop:10,borderTop:"0.5px solid var(--mf-border)"}}>
            <label style={{fontSize:10,color:"var(--mf-muted)",display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>
              <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{cursor:"pointer"}}/> Alle auswählen
            </label>
            <div style={{flex:1}}/>
            <span style={{fontSize:10,color:"var(--mf-muted)"}}>{checked.length} ausgewählt · Kanal:</span>
            <select value={kanal} onChange={e=>setKanal(e.target.value)} style={{fontSize:10,padding:"4px 7px",borderRadius:5,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-text)",fontFamily:"inherit"}}>
              {[["E-Mail","E-Mail (versendet wirklich)"],["SMS","SMS (nur Vermerk)"],["Brief","Brief (nur Vermerk)"],["Anruf-Liste","Anruf-Liste (nur Vermerk)"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
            <Btn color="g" onClick={()=>{if(checked.length){setSent(false);setSendOpen(true);}}}>Recall senden ({checked.length})</Btn>
          </div>
        )}
      </Card>

      {showNewRecall&&(
        <Overlay title="Recall anlegen" icon="ti-bell-plus" onClose={()=>setShowNewRecall(false)} width={420}>
          <div style={{display:"flex",flexDirection:"column"}}>
            <PatientPicker label="Patient" value={newRecall.name}
              onChange={v=>setNewRecall(n=>({...n,name:v}))}
              onPick={p=>setNewRecall(n=>({...n,name:`${p.nachname}, ${p.vorname}`,dob:p.dob||"",behandler:p.treat||n.behandler,lastPzr:n.lastPzr||((p.last&&p.last!=="—")?p.last:"")}))}/>
            <Input label="Letzte PZR (TT.MM.JJ)" value={newRecall.lastPzr} onChange={v=>setNewRecall(n=>({...n,lastPzr:v}))} placeholder="z.B. 12.01.26"/>
            <Select label="Intervall" value={String(newRecall.intervalTage)} onChange={v=>setNewRecall(n=>({...n,intervalTage:parseInt(v)||182}))}
              options={[{value:"90",label:"3 Monate (Paro/UPT)"},{value:"140",label:"4–5 Monate"},{value:"182",label:"6 Monate (Standard)"},{value:"365",label:"12 Monate"}]}/>
            <Select label="Behandler" value={newRecall.behandler} onChange={v=>setNewRecall(n=>({...n,behandler:v}))} options={getBehandler().length?getBehandler().map(b=>b.name):["—"]}/>
            <div style={{display:"flex",gap:10,marginTop:4}}>
              <Btn color="g" lg style={{flex:1}} onClick={async()=>{
                if(!newRecall.name||!newRecall.lastPzr){ toast("Patient und letzte PZR angeben.","r"); return; }
                try{
                  const rows=await addRecall(newRecall);
                  setRecalls(rows); setShowNewRecall(false);
                  setNewRecall({name:"",dob:"",lastPzr:"",intervalTage:182,behandler:getBehandler()[0]?.name||""});
                  toast("Recall angelegt");
                }catch{ toast("Anlegen fehlgeschlagen — Verbindung prüfen.","r"); }
              }}>Anlegen</Btn>
              <Btn lg onClick={()=>setShowNewRecall(false)}>Abbrechen</Btn>
            </div>
          </div>
        </Overlay>
      )}

      <Insight>Selektiv statt Gießkanne: Bei Patienten mit <strong>niedrigem Score</strong> lohnt der persönliche Anruf mehr als eine SMS — die Scorecard hilft beim Priorisieren.</Insight>

      {sendOpen&&(
        <Overlay title="Recall senden" sub={sent?`${sentCount} Patienten · Kanal: ${kanal}`:`${checked.length} Patienten · Kanal: ${kanal}`} icon="ti-bell-ringing" onClose={()=>setSendOpen(false)} width={440}>
          {sent?(
            <div style={{textAlign:"center",padding:"16px 0"}}>
              <div style={{marginBottom:10}}><Picto.Check size={40}/></div>
              <div style={{fontSize:13,fontWeight:500,color:"var(--mf-text)",marginBottom:4}}>Recall versendet!</div>
              <div style={{fontSize:11,color:"var(--mf-muted)",marginBottom:16}}>{/mail/i.test(kanal)?`${sentCount} Patienten wurden per E-Mail zur PZR eingeladen und als „Kontaktiert“ markiert.`:`${sentCount} Patienten als „Kontaktiert (${kanal})“ vermerkt — der ${kanal}-Versand selbst läuft außerhalb von dida.`}</div>
              <Btn color="g" lg onClick={()=>setSendOpen(false)}>Schließen</Btn>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{background:"var(--mf-bg-alt)",borderRadius:6,padding:"8px 10px",fontSize:11,color:"var(--mf-text)",maxHeight:90,overflowY:"auto"}}>
                {recalls.filter(r=>checked.includes(r.id)).map(r=>r.name).join(" · ")}
              </div>
              {/mail/i.test(kanal) ? (<>
                <Input label="Betreff" value={recallSubject} onChange={setRecallSubject} style={{marginBottom:0}}/>
                <Textarea label="Nachricht (wird so versendet)" value={recallText} onChange={setRecallText} rows={9} style={{marginBottom:0}}/>
                <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:-4}}>{"{name}"} wird automatisch durch den Vornamen des Patienten ersetzt.</div>
              </>) : <RecallMsg kanal={kanal}/>}
              <div style={{display:"flex",gap:10}}>
                <Btn color="g" lg style={{flex:1}} onClick={send}>Jetzt senden</Btn>
                <Btn lg onClick={()=>setSendOpen(false)}>Abbrechen</Btn>
              </div>
            </div>
          )}
        </Overlay>
      )}
    </div>
  );
}

function RecallMsg({ kanal }) {
  const [msg,setMsg] = useState("Liebe Patientin, lieber Patient,\n\nIhre nächste professionelle Zahnreinigung ist fällig. Buchen Sie bequem online oder rufen Sie uns an.\n\n" + praxisAbsender());
  return <Textarea label={`Nachricht (${kanal})`} value={msg} onChange={setMsg} rows={4} style={{marginBottom:0}}/>;
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: SCORECARD
// ═══════════════════════════════════════════════════════════════════════════

function ScorecardTab() {
  const [rawPatients, setRawPatients] = useState(loadPatientsCache);
  useEffect(()=>{ let on=true; listPatients().then(p=>{ if(on) setRawPatients(p); }).catch(()=>{}); return ()=>{ on=false; }; },[]);
  const patients = useMemo(()=>rawPatients.map(p=>({...p,...scoreFactors(p)})),[rawPatients]);
  const [sortAsc, setSortAsc] = useState(true);
  const [gradeF, setGradeF] = useState("");
  const [sel, setSel] = useState(null);

  const sorted = useMemo(()=>[...patients].sort((a,b)=>sortAsc?a.score-b.score:b.score-a.score),[patients,sortAsc]);
  const filtered = gradeF?sorted.filter(p=>scoreMeta(p.score).grade===gradeF):sorted;
  const { counts, avg } = useMemo(()=>scoreCounts(patients),[patients]);
  const T = useTones();
  const GRADE_TONE = { A:"g", B:"b", C:"a", D:"r" };
  const distSegs = [
    { key:"A", label:"A · Top",       tone:"g", count:counts.A },
    { key:"B", label:"B · Stabil",    tone:"b", count:counts.B },
    { key:"C", label:"C · Gefährdet", tone:"a", count:counts.C },
    { key:"D", label:"D · Kritisch",  tone:"r", count:counts.D },
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      <div style={{display:"grid",gridTemplateColumns:"minmax(140px,190px) 1fr",gap:9,alignItems:"stretch"}}>
        <StatTile tone="n" v={`Ø ${avg}`} l="Praxis-Durchschnitt" d={`${patients.length} Patienten`} active={!gradeF} onClick={()=>setGradeF("")}/>
        <Card>
          <CardHead title="Patienten-Verteilung" icon="ti-chart-bar" right={<span style={{fontSize:10,color:"var(--mf-muted)"}}>Klick filtert die Liste</span>}/>
          <DistBar segments={distSegs} total={patients.length} activeKey={gradeF} onPick={setGradeF}/>
          {counts.D>0&&<div style={{marginTop:10}}><Insight tone="r"><strong>{counts.D}</strong> {counts.D===1?"Patient ist":"Patienten sind"} kritisch (Segment D) — Reaktivierungsangebot senden.</Insight></div>}
        </Card>
      </div>

      <Card>
        <CardHead title={gradeF?`Patienten — Segment ${gradeF}`:"Alle Patienten nach Score"} icon="ti-target"
          right={<button onClick={()=>setSortAsc(a=>!a)} style={{fontSize:10,padding:"3px 9px",borderRadius:5,border:"0.5px solid var(--mf-border2)",background:"var(--mf-bg-alt)",color:"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{sortAsc?"Kritischste zuerst ↑":"Beste zuerst ↓"}</button>}/>
        <Tbl heads={["Patient","Score","Status","Letzter Termin","PZR","Empfehlung"]}>
          {filtered.map(p=>{
            const m = scoreMeta(p.score);
            return (
              <tr key={p.id} onClick={()=>setSel(p)} style={{cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background="var(--mf-bg-alt)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <Td bold style={{borderLeft:`3px solid ${T[GRADE_TONE[m.grade]].bar}`,paddingLeft:9}}>{p.nachname}, {p.vorname}</Td>
                <Td><ScoreBadge score={p.score}/></Td>
                <Td><Sp color={{Aktiv:"g",Erinnern:"a",Inaktiv:"r"}[p.status]||"b"}>{p.status}</Sp></Td>
                <Td color="var(--mf-muted)">{p.last}</Td>
                <Td color="var(--mf-muted)">{p.pzr}</Td>
                <Td color={m.col} style={{fontSize:10}}>{m.empf}</Td>
              </tr>
            );
          })}
        </Tbl>
      </Card>

      <Insight>Der Score bewertet Bindung & Umsatzpotenzial je Patient (0–100): Status, Prophylaxe-Teilnahme, Besuchsfrequenz, Recall-Treue und Herkunft. Er ist auch in der <strong>Patientenliste</strong> als Spalte sichtbar.</Insight>

      {sel&&(
        <Overlay title={`${sel.nachname}, ${sel.vorname}`} sub={`Score-Aufschlüsselung · ${sel.score} / 100`} icon="ti-target" onClose={()=>setSel(null)} width={440}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <ScoreBadge score={sel.score} showLabel/>
            <div style={{flex:1,height:8,background:"var(--mf-bg-alt)",borderRadius:4,overflow:"hidden"}}>
              <div style={{height:8,width:`${sel.score}%`,background:scoreMeta(sel.score).bar,borderRadius:4}}/>
            </div>
          </div>
          <SH t="Bewertungsfaktoren"/>
          {sel.factors.map((f,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"5px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
              <span style={{color:"var(--mf-text)"}}>{f.label}</span>
              <span style={{fontWeight:600,color:f.pts>0?"#0F6E56":f.pts<0?"#993C1D":"var(--mf-muted)"}}>{f.pts>0?`+${f.pts}`:f.pts}</span>
            </div>
          ))}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"6px 0",fontWeight:600}}>
            <span style={{color:"var(--mf-text)"}}>Gesamt (Basis 50)</span>
            <span style={{color:scoreMeta(sel.score).col}}>{sel.score} / 100</span>
          </div>
          <div style={{marginTop:8}}>
            <Insight tone={sel.score>=60?"g":sel.score>=40?"a":"r"}><strong>Empfehlung:</strong> {scoreMeta(sel.score).empf}</Insight>
          </div>
        </Overlay>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: FEEDBACK (Behandlerebene)
// ═══════════════════════════════════════════════════════════════════════════

function FeedbackTab() {
  const [view, setView] = useState("kennzahlen"); // Kennzahlen | Bewertungen (gemergter Bewertungen-Reiter)
  const reviews = loadReviews();
  const m = feedbackMetrics(reviews);
  const unbeantwortet = reviews.filter(r=>!r.antwort).length;
  // Team-eNPS aus dem anonymen Team-Puls (Dashboard-Karte)
  const [pulse, setPulse] = useState(null);
  useEffect(()=>{ loadPulse().then(setPulse).catch(()=>setPulse([])); },[]);
  const curPulse = (pulse||[]).find(r=>r.periode===pulsePeriode());
  const pulseScores = curPulse?.payload?.scores||[];

  const switcher = (
    <div style={{display:"flex",gap:4}}>
      {[["kennzahlen","Kennzahlen"],["bewertungen","Bewertungen & Umfrage"]].map(([k,l])=>(
        <FilterPill key={k} active={view===k} onClick={()=>setView(k)}>{l}</FilterPill>
      ))}
    </div>
  );

  if (view==="bewertungen") return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      {switcher}
      <BewertungenScreen/>
    </div>
  );

  if (!m.ok) return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      {switcher}
      <Card><EmptyState hint={m.hint}/></Card>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      {switcher}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7}}>
        <Tile v={`${String(m.avg).replace(".",",")} ★`} l="Ø Patientenbewertung" d={`aus ${m.count} Bewertungen`} dc="up" onClick={()=>{}}/>
        <Tile v={String(m.count)} l="Bewertungen gesamt" d="Umfrage & Portale" onClick={()=>{}}/>
        <Tile v={String(unbeantwortet)} l="Ohne Antwort" d={unbeantwortet>0?"bitte reagieren":"alles beantwortet"} dc={unbeantwortet>0?"dn":"up"} onClick={()=>{}}/>
        <Tile v={`${Math.round((m.dist[0].count/m.count)*100)} %`} l="5-Sterne-Anteil" dc="up" onClick={()=>{}}/>
        <Tile v={pulseScores.length>=MIN_VOTES?String(eNPS(pulseScores)):"—"} l="Team-eNPS (Monat)" d={pulseScores.length?`${pulseScores.length} Stimmen${pulseScores.length<MIN_VOTES?` — ab ${MIN_VOTES} auswertbar`:""}`:"Team-Puls im Dashboard"} dc={pulseScores.length>=MIN_VOTES&&eNPS(pulseScores)>=0?"up":undefined} onClick={()=>{}}/>
      </div>

      <Card>
        <CardHead title="Bewertungsverteilung" icon="ti-star"/>
        {m.dist.map(d=><HBar key={d.stars} label={`${d.stars} Sterne`} value={d.count} max={Math.max(...m.dist.map(x=>x.count),1)} color={d.stars>=4?"#0F6E56":d.stars===3?"#BA7517":"#D85A30"} valStr={String(d.count)}/>)}
      </Card>

      <Card>
        <CardHead title="Feedback je Behandler" icon="ti-user-star"/>
        {(()=>{
          const mitBeh = reviews.filter(r=>r.behandler);
          if(!mitBeh.length) return <div style={{fontSize:11,color:"var(--mf-muted)",padding:"6px 0",lineHeight:1.5}}>Entsteht aus der internen Umfrage: Im Bewertungs-Bereich den Umfrage-Link mit Behandler-Zuordnung erzeugen — jede Antwort landet hier.</div>;
          const byBeh = {};
          mitBeh.forEach(r=>{ (byBeh[r.behandler]=byBeh[r.behandler]||[]).push(r); });
          const rows = Object.entries(byBeh).map(([b,list])=>({ b, n:list.length,
            avg: list.reduce((s,r)=>s+(r.stars||0),0)/list.length,
            nps: (()=>{ const ns=list.map(r=>r.nps).filter(x=>x!=null); if(!ns.length) return null;
              return Math.round((ns.filter(x=>x>=9).length-ns.filter(x=>x<=6).length)/ns.length*100); })(),
          })).sort((a,b)=>b.avg-a.avg);
          return rows.map(r=>(
            <HBar key={r.b} label={`${r.b} (${r.n})`} value={r.avg} max={5} color={r.avg>=4?"#0F6E56":r.avg>=3?"#BA7517":"#D85A30"}
              valStr={`${r.avg.toFixed(1).replace(".",",")} ★${r.nps!=null?` · NPS ${r.nps}`:""}`}/>
          ));
        })()}
      </Card>
      <Insight tone="a">Die <strong>Team-Stimmung (eNPS)</strong> kommt aus dem anonymen Team-Puls im Dashboard — Auswertung im Mitarbeiter-Reiter.</Insight>
    </div>
  );
}

function OverviewTab({ onOpenTab, onNavigate, viewModes, onShrink, mobile }) {
  const [detail, setDetail] = useState(null);
  const [statsRows, setStatsRows] = useState([]);
  const [warm, setWarm] = useState(0);
  // Alle Quellen selbst laden (füllt die Caches) — sonst wären die Kacheln
  // in einer frischen Browser-Session leer, obwohl die Daten in der DB liegen
  useEffect(() => {
    let on = true;
    Promise.allSettled([
      listPatients(), listHkps(), listRecalls(), listAptsStore(), reviewStore.list(),
      // Alle Statistik-Zeilen laden (Umsatz + Leistungsziffern) — die
      // Rechenschicht filtert selbst nach Typ.
      listStats().then(r => { if (on) setStatsRows(r); }),
    ]).then(() => { if (on) setWarm(w => w + 1); });
    return () => { on = false; };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const m = useMemo(() => computeMetrics(statsRows), [statsRows, warm]);

  // Kachel-Werte aus den berechneten Metriken; empty = ehrlicher Leerzustand
  const tiles = useMemo(() => {
    const E = (hint) => ({ empty: true, v: "—", d: "Keine Datenquelle", hint });
    const t = {};
    t.hkp = m.hkp.ok
      ? { v: m.hkp.quote != null ? `${m.hkp.quote} %` : `${m.hkp.imUmlauf} offen`, d: m.hkp.quote != null ? `${m.hkp.entschieden} Entscheidungen` : "noch keine Entscheidung", dc: "up" }
      : E(m.hkp.hint);
    t.umsatzart = m.umsatz.ok
      ? { v: m.umsatz.fmt, d: m.umsatz.deltaPct != null ? `${m.umsatz.deltaPct >= 0 ? "↑" : "↓"} ${Math.abs(m.umsatz.deltaPct)} % vs. ${m.umsatz.prev.periode}` : `Periode ${m.umsatz.latest.periode}`, dc: m.umsatz.deltaPct == null || m.umsatz.deltaPct >= 0 ? "up" : "dn" }
      : E(m.umsatz.hint);
    t.leistungen = m.leistungen.ok
      ? { v: m.leistungen.gozAnteil != null ? `${m.leistungen.gozAnteil} % GOZ` : `${m.leistungen.zifferCount} Ziffern`,
          d: m.leistungen.mitBetrag ? `${eur(Math.round(m.leistungen.gesamtBetrag))} Honorar · Top: ${m.leistungen.topBetrag[0].ziffer}` : `Top: ${m.leistungen.topAnzahl[0].ziffer} (${m.leistungen.topAnzahl[0].anzahl}×)`,
          dc: "up" }
      : E(m.leistungen.hint);
    t.kassenmix = m.kassenmix.ok
      ? { v: `${m.kassenmix.privatPct} % privat`, d: m.kassenmix.umsatzPrivat != null ? `${m.kassenmix.umsatzPrivat} % des Umsatzes` : `${m.kassenmix.verteilung[0].kv} am häufigsten`, dc: "up" }
      : E(m.kassenmix.hint);
    t.clv = m.umsProPatient.ok
      ? { v: m.umsProPatient.fmt, d: `${m.umsProPatient.aktive} aktive Patienten · ${m.umsProPatient.periode}`, dc: "up" }
      : E(m.umsProPatient.hint);
    t.noshow = m.ausfall.ok
      ? { v: m.ausfall.fmt, d: `${m.ausfall.abgesagt} von ${m.ausfall.gesamt} Terminen`, dc: "dn" }
      : E(m.ausfall.hint);
    t.recall = m.recall.ok
      ? { v: m.recall.ruecklauf != null ? `${m.recall.ruecklauf} %` : `${m.recall.offen} offen`, d: `${m.recall.ueberfaellig} überfällig`, dc: m.recall.ueberfaellig > 0 ? "dn" : "up" }
      : E(m.recall.hint);
    t.score = m.score.ok
      ? { v: `Ø ${m.score.avg}`, d: `${m.score.counts.D} kritisch · ${m.score.counts.C} gefährdet`, dc: m.score.counts.D > 0 ? "dn" : "up" }
      : E(m.score.hint);
    t.demografie = m.demografie.ok
      ? { v: `Ø ${m.demografie.avg} J.`, d: `Größte Gruppe: ${m.demografie.groesste.label.replace(" Jahre", "")} (${m.demografie.groesste.pct} %)` }
      : E(m.demografie.hint);
    t.auslastung = m.auslastung.ok
      ? { v: `${m.auslastung.pct} %`, d: `${m.auslastung.termine} Termine diese Woche`, dc: "up" }
      : E(m.auslastung.hint);
    t.behandler = m.auslastung.ok
      ? (() => { const top = [...m.auslastung.beh].sort((a, b) => b.pct - a.pct)[0]; return { v: `${top.pct} %`, d: `Top: ${top.name}`, dc: "up" }; })()
      : E(m.auslastung.hint);
    t.feedback = m.feedback.ok
      ? { v: `${String(m.feedback.avg).replace(".", ",")} ★`, d: `${m.feedback.count} Bewertungen`, dc: "up" }
      : E(m.feedback.hint);
    t.churn = m.churn.ok
      ? { v: `${m.churn.pct} %`, d: `${m.churn.risk} Risiko-Patienten`, dc: m.churn.risk > 0 ? "dn" : "up" }
      : E(m.churn.hint);
    ["igel", "wartezeit", "wartezimmer", "dauer"].forEach(id => { t[id] = E(NOT_YET[id]); });
    return t;
  }, [m]);

  // Mobil: kompakte Ein-Spalten-Übersicht mit den Kernkennzahlen
  if (mobile) {
    const MOB = ["umsatzart","hkp","auslastung","noshow","recall","churn"];
    return (
      <div style={{display:"flex",flexDirection:"column",gap:7}}>
        {MOB.map(id=>(
          <div key={id} title={KPI_INFO[id]||""}>
            <Tile v={tiles[id].v} l={KPI_META[id].l} d={tiles[id].d} dc={tiles[id].dc} onClick={()=>setDetail(id)}/>
          </div>
        ))}
        {detail&&<KpiDetail kpi={detail} m={m} tile={tiles[detail]} onClose={()=>setDetail(null)} onNavigate={onNavigate}/>}
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {OVERVIEW_SECTIONS.map(sec=>{
        const small = sec.tiles.filter(id=>viewModes[id]!=="detail");
        const big   = sec.tiles.filter(id=>viewModes[id]==="detail");
        return (
          <div key={sec.label}>
            <SH t={sec.label}/>
            {small.length>0&&(
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:7,marginBottom:7}}>
                {small.map(id=>(
                  <div key={id} title={KPI_INFO[id]||""}>
                    <Tile v={tiles[id].v} l={KPI_META[id].l} d={tiles[id].d} dc={tiles[id].dc}
                      onClick={()=>id==="score"&&!tiles[id].empty?onOpenTab("scorecard"):setDetail(id)}/>
                  </div>
                ))}
              </div>
            )}
            {big.length>0&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:7}}>
                {big.map(id=>(
                  <Card key={id}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9,gap:8}}>
                      <span style={{fontSize:12,fontWeight:500,display:"flex",alignItems:"center",gap:5,color:"var(--mf-text)",minWidth:0}}>
                        <i className={`ti ${KPI_META[id].icon}`} style={{fontSize:13,color:"var(--mf-primary)",flexShrink:0}}/>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{KPI_META[id].l}</span>
                      </span>
                      <span style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                        <span style={{fontSize:13,fontWeight:600,color:"var(--mf-text)"}}>{tiles[id].v}</span>
                        <span style={{fontSize:10,color:tiles[id].dc==="up"?"var(--mf-primary)":tiles[id].dc==="dn"?"var(--mf-neg)":"var(--mf-muted)"}}>{tiles[id].d}</span>
                        <i className="ti ti-arrows-minimize" title="Als Kachel anzeigen" onClick={()=>onShrink(id)}
                          style={{fontSize:13,color:"var(--mf-muted)",cursor:"pointer"}}/>
                      </span>
                    </div>
                    <KpiBody kpi={id} m={m} onNavigate={onNavigate}/>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Potenzial-Rechner: Modellrechnung mit den ECHTEN Praxiszahlen —
          zeigt, was kleine Verbesserungen der Kern-Quoten wert wären. */}
      <div>
        <SH t="Potenzial"/>
        <Card>
          <CardHead title="Potenzial-Rechner" icon="ti-trending-up"/>
          {(()=>{
            const rows = [];
            const perPat = m.umsProPatient.ok ? m.umsProPatient.value : null;
            // Hebel 1: HKP-Annahmequote +5 Punkte (wertbasiert)
            if (m.hkp.ok && m.hkp.entschVol > 0) {
              const vq = Math.round(m.hkp.acceptedVol / m.hkp.entschVol * 100);
              rows.push({ n:"01", t:"HKP-Annahme", ist:`${vq} %`, ziel:`${Math.min(vq+5,100)} %`,
                wert: eur(Math.round(m.hkp.entschVol * 0.05)),
                d:`+5 Punkte Annahmequote auf dein entschiedenes HKP-Volumen (${eur(m.hkp.entschVol)})` });
            } else rows.push({ n:"01", t:"HKP-Annahme", hint:"Braucht entschiedene HKPs im HKP-Manager." });
            // Hebel 2: No-Show -3 Punkte
            if (m.ausfall.ok) {
              const senkung = Math.min(3, m.ausfall.quote);
              const frei = Math.round(m.ausfall.gesamt * senkung / 100 * 12); // 30-Tage-Basis aufs Jahr
              rows.push({ n:"02", t:"No-Shows", ist:`${m.ausfall.quote} %`, ziel:`${m.ausfall.quote - senkung} %`,
                wert: perPat ? eur(Math.round(frei * perPat)) : `~${frei} Termine/Jahr`,
                d:`-${senkung} Punkte Ausfallquote ≈ ${frei} zusätzliche Termine im Jahr${perPat?` × Ø ${eur(Math.round(perPat))} je Patient`:""}` });
            } else rows.push({ n:"02", t:"No-Shows", hint:"Braucht vergangene Termine im Terminplan." });
            // Hebel 3: Reaktivierung (überfällige Recalls + Churn-Risiko)
            const pool = (m.recall.ok ? m.recall.ueberfaellig : 0) + (m.churn.ok ? m.churn.risk : 0);
            if (pool > 0) {
              const gewonnen = Math.max(1, Math.round(pool * 0.3));
              rows.push({ n:"03", t:"Reaktivierung", ist:`${pool} Patienten`, ziel:`${gewonnen} zurück`,
                wert: perPat ? eur(Math.round(gewonnen * perPat)) : `~${gewonnen} Patienten`,
                d:`${pool} überfällige/inaktive Patienten — schon 30 % Rücklauf bringen ${gewonnen} zurück` });
            } else rows.push({ n:"03", t:"Reaktivierung", hint:"Aktuell keine überfälligen Recalls oder Churn-Risiko-Patienten — gut so!" });
            return (<>
              {rows.map(r=>(
                <div key={r.n} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"0.5px solid var(--mf-border)",flexWrap:"wrap"}}>
                  <span style={{fontSize:10,fontWeight:600,color:"var(--mf-muted)",width:20,flexShrink:0}}>{r.n}</span>
                  <span style={{fontSize:12,fontWeight:500,color:"var(--mf-text)",width:110,flexShrink:0}}>{r.t}</span>
                  {r.hint ? (
                    <span style={{fontSize:11,color:"var(--mf-muted)",flex:1}}>{r.hint}</span>
                  ) : (<>
                    <span style={{fontSize:11,color:"var(--mf-muted)",flexShrink:0}}>{r.ist} → <strong style={{color:"var(--mf-text)"}}>{r.ziel}</strong></span>
                    <span style={{fontSize:11,color:"var(--mf-muted)",flex:1,minWidth:120}}>{r.d}</span>
                    <span style={{fontSize:13,fontWeight:600,color:"var(--mf-primary)",flexShrink:0}}>+{r.wert}</span>
                  </>)}
                </div>
              ))}
              <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:7}}>Modellrechnung auf Basis deiner echten Kennzahlen — Orientierung, keine Prognose.</div>
            </>);
          })()}
        </Card>
      </div>

      {detail&&<KpiDetail kpi={detail} m={m} tile={tiles[detail]} onClose={()=>setDetail(null)} onNavigate={onNavigate}/>}
    </div>
  );
}

export default function TrackingScreen({ onNavigate, tab: tabProp, onTabChange, sidebarNav }) {
  const th = THEMES[useTheme()];
  // Tabs können von der Sidebar gesteuert werden (kontrolliert); sonst intern.
  const [tabState, setTabState] = useState("uebersicht");
  const tab = tabProp !== undefined ? tabProp : tabState;
  const setTab = onTabChange || setTabState;
  const [isMobileT,setIsMobileT]=useState(()=>typeof window!=="undefined"&&window.innerWidth<640);
  useEffect(()=>{ const f=()=>setIsMobileT(window.innerWidth<640); window.addEventListener("resize",f); return ()=>window.removeEventListener("resize",f); },[]);
  useEffect(()=>{ if(isMobileT&&tab!=="uebersicht") setTab("uebersicht"); },[isMobileT,tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [viewModes, setViewModes] = useState(()=>{
    try{const s=localStorage.getItem("dida_tracking_views");if(s)return JSON.parse(s);}catch{}
    return {};
  });
  useEffect(()=>{try{localStorage.setItem("dida_tracking_views",JSON.stringify(viewModes));}catch{}},[viewModes]);
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false);

  const TABS = [
    { k:"uebersicht", l:"Übersicht",    icon:"ti-layout-grid" },
    { k:"hkp",        l:"HKP-Manager",  icon:"ti-file-check" },
    { k:"recall",     l:"Recall",       icon:"ti-bell-ringing" },
    { k:"scorecard",  l:"Scorecard",    icon:"ti-target" },
    { k:"feedback",   l:"Feedback",     icon:"ti-star" },
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:9}}>
      <div style={{display:isMobileT?"none":"flex",alignItems:"center",gap:4}}>
        {!sidebarNav&&TABS.map(tb=>(
          <button key={tb.k} onClick={()=>setTab(tb.k)} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,padding:"5px 12px",borderRadius:7,border:"0.5px solid",borderColor:tab===tb.k?th.primary:"var(--mf-border2)",background:tab===tb.k?th.primary:"transparent",color:tab===tb.k?th.onPrimary:"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit",fontWeight:tab===tb.k?500:400}}>
            <i className={`ti ${tb.icon}`} style={{fontSize:12}}/>{tb.l}
          </button>
        ))}
        <div style={{flex:1}}/>
        {tab==="uebersicht"&&(
          <button onClick={()=>setViewSettingsOpen(true)} title="Ansicht anpassen"
            style={{display:"flex",alignItems:"center",gap:5,fontSize:11,padding:"5px 11px",marginLeft:4,borderRadius:7,border:"0.5px solid var(--mf-border2)",background:"transparent",color:"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>
            <i className="ti ti-settings" style={{fontSize:13}}/>Einstellungen
          </button>
        )}
      </div>

      {tab==="uebersicht"&&<OverviewTab mobile={isMobileT} onOpenTab={setTab} onNavigate={onNavigate} viewModes={viewModes} onShrink={id=>setViewModes(p=>({...p,[id]:"tile"}))}/>}
      {tab==="hkp"&&<HkpTab/>}
      {tab==="recall"&&<RecallTab/>}
      {tab==="scorecard"&&<ScorecardTab/>}
      {tab==="feedback"&&<FeedbackTab/>}

      {viewSettingsOpen&&(
        <Overlay title="Ansicht anpassen" sub="Pro Kennzahl wählen: kompakte Kachel oder Detailansicht mit Diagramm" icon="ti-settings" onClose={()=>setViewSettingsOpen(false)} width={480}>
          {OVERVIEW_SECTIONS.map(sec=>(
            <div key={sec.label} style={{marginBottom:8}}>
              <SH t={sec.label}/>
              {sec.tiles.map(id=>{
                const mode = viewModes[id]==="detail"?"detail":"tile";
                return (
                  <div key={id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"0.5px solid var(--mf-border)"}}>
                    <i className={`ti ${KPI_META[id].icon}`} style={{fontSize:13,color:"var(--mf-primary)",width:16,flexShrink:0}}/>
                    <span style={{flex:1,fontSize:11,color:"var(--mf-text)"}}>{KPI_META[id].l}</span>
                    <div style={{display:"flex",border:"0.5px solid var(--mf-border2)",borderRadius:6,overflow:"hidden"}}>
                      {[["tile","Kachel"],["detail","Diagramm"]].map(([m,l])=>(
                        <button key={m} onClick={()=>setViewModes(p=>({...p,[id]:m}))}
                          style={{fontSize:10,padding:"3px 10px",border:"none",background:mode===m?th.primary:"transparent",color:mode===m?th.onPrimary:"var(--mf-muted)",cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div style={{display:"flex",gap:6,marginTop:10}}>
            <Btn onClick={()=>setViewModes({})}>Alle als Kacheln</Btn>
            <Btn onClick={()=>{const all={};OVERVIEW_SECTIONS.forEach(s=>s.tiles.forEach(id=>{all[id]="detail";}));setViewModes(all);}}>Alle als Diagramme</Btn>
            <Btn color="g" onClick={()=>setViewSettingsOpen(false)}>Fertig</Btn>
          </div>
        </Overlay>
      )}
    </div>
  );
}
